/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : validateFlow.js
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
export function validateFlow(nodes, edges) {
  const errors = [];
  const invalidNodeIds = new Set();
  const invalidEdgeIds = new Set();

  const nodeMap = Object.fromEntries(nodes.map((n) => [n.id, n]));
  const outgoing = {};
  const incoming = {};

  for (const e of edges) {
    outgoing[e.source] ||= [];
    incoming[e.target] ||= [];
    outgoing[e.source].push(e);
    incoming[e.target].push(e);
  }

  for (const q of nodes.filter((n) => n.type === "question")) {
    for (const a of q.data?.answers || []) {
      const connected = edges.some(
        (e) => e.source === q.id && e.sourceHandle === a.id,
      );
      if (!connected) {
        errors.push({
          level: "error",
          message: `Question "${q.data?.questionText || "Untitled"}": option "${a.label}" is not connected`,
          nodeId: q.id,
        });
        invalidNodeIds.add(q.id);
      }
    }
  }

  for (const e of nodes.filter((n) => n.type === "end")) {
    if (!e.data?.outcome?.value) {
      errors.push({
        level: "error",
        message: "End node missing outcome",
        nodeId: e.id,
      });
      invalidNodeIds.add(e.id);
    }
  }

  const start = nodes.find((n) => n.type === "start");
  if (!start) {
    errors.push({
      level: "error",
      message: "Missing Start node",
    });
    return { errors, invalidNodeIds: [...invalidNodeIds], invalidEdgeIds: [] };
  }

  const visited = new Set();

  function dfs(nodeId, path = []) {
    if (visited.has(nodeId)) return;
    visited.add(nodeId);

    const node = nodeMap[nodeId];
    const outs = outgoing[nodeId] || [];

    if (outs.length === 0 && node?.type !== "end") {
      errors.push({
        level: "error",
        message: `Path ends at "${node?.type}" node without End`,
        nodeId: nodeId,
      });
      invalidNodeIds.add(nodeId);
      for (const e of incoming[nodeId] || []) {
        invalidEdgeIds.add(e.id);
      }
    }

    for (const e of outs) {
      dfs(e.target, [...path, nodeId]);
    }
  }

  dfs(start.id);

  const reachesEnd = nodes
    .filter((n) => n.type === "end")
    .some((end) => incoming[end.id]?.length);

  if (!reachesEnd) {
    errors.push({
      level: "error",
      message: "No path from Start reaches an End node",
      nodeId: start.id,
    });
    invalidNodeIds.add(start.id);
  }

  return {
    errors,
    invalidNodeIds: [...invalidNodeIds],
    invalidEdgeIds: [...invalidEdgeIds],
  };
}
